import React, { useEffect, useMemo, useState } from 'react'
import societe from 'assets/pictures/logos/societe.png'
import logoSandre from 'assets/pictures/logo_sandre.png'
import Input from 'components/forms/Input'
import CartographyPanel from 'components/map/CartographyPanel'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
    nbPerPageLabel,
    REFERENCIAL_TYPE_NAME,
    REFERENTIAL_JOBSYNC_ID,
} from 'referencial/constants/ReferencialConstants'
import i18n from 'simple-react-i18n'
import { getDataWithHeaders } from 'utils/ExportDataUtil'
import AdministrationAction from '../../../../administration/actions/AdministrationAction'
import Select from '../../../../components/forms/Select'
import { sandreContributorDefaultLink, societeDefaultUrl } from '../../../../conf/SieauConstants'
import HomeAction from '../../../../home/actions/HomeAction'
import {
    CONTRIBUTOR_LINK,
    PATH_REFERENCIAL,
    PATH_REFERENCIAL_CONTRIBUTOR,
    PATH_REFERENCIAL_CONTRIBUTOR_NEW,
    PATH_REFERENCIAL_LINK,
    PATH_REFERENCIAL_RPQS_LINK,
} from '../../../../home/constants/RouteConstants'
import { default as ReferencialAction } from '../../../action/ReferencialAction'
import { getExportReferencialData, getReferencialData, reloadJobSync } from '../../../util/ReferencialUtils'
import CityAction from '../../city/actions/CityAction'
import CountryAction from '../../country/actions/CountryAction'
import ContributorAction from '../actions/ContributorAction'
import { CONTRIBUTOR, MAP } from '../constants/ContributorConstants'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import { push } from '@lagunovsky/redux-react-router'
import { GROUP } from 'referencial/components/contact/constants/ContactConstants'
import ContributorsGroupsApp from './ContributorsGroupsApp'
import ProgressCard from 'components/card/ProgressCard'
import { Button, Card, CardContent, Grid, Stack } from '@mui/material'
import Tabs from 'components/Tabs'
import { CardTitle } from 'components/card/NewCard'
import { CardTable } from 'components/datatable/NewTable'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import JobSyncStatus from '../../JobSyncStatus'
import PropTypes from 'prop-types'

const ContributorsPanel = ({ rpqs }) => {
    const {
        contributorsTable,
        cities,
        countries,
        sandreCodes,
        globalResearch,
        applicationSettings,
        referencialStatus,
    } = useSelector(store => ({
        contributorsTable: store.ContributorReducer.contributorsTable,
        cities: store.CityReducer.cities,
        countries: store.CountryReducer.countries,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        globalResearch: store.HomeReducer.globalResearch,
        applicationSettings: store.AdministrationReducer.applicationSettings,
        referencialStatus: store.ReferencialReducer.referencialStatus,
    }), shallowEqual)

    const [filter, setFilter] = useLocalStorage(`referencial_${REFERENCIAL_TYPE_NAME.contributor}`, {})
    const [dataLoaded, setDataLoaded] = useState(false)
    const [jobSync, setJobSync] = useState(undefined)

    useTitle(() => {
        if (rpqs) {
            return [{
                title: i18n.rpqs,
                href: 'rpqs',
            }, {
                title: i18n.RPQSReferencials,
                href: PATH_REFERENCIAL_RPQS_LINK,
            }, {
                title: i18n.contributors,
                href: PATH_REFERENCIAL_RPQS_LINK + CONTRIBUTOR_LINK,
            }]
        }
        return [{
            title: i18n.referencials,
            href: PATH_REFERENCIAL,
        }, {
            title: i18n.contributors,
            href: PATH_REFERENCIAL_CONTRIBUTOR,
        }]
    }, [rpqs])

    useEffect(() => {
        reloadJobSync(REFERENTIAL_JOBSYNC_ID.contributor, setJobSync)
    }, [])

    useEffect(() => {
        if (jobSync && jobSync.executions[0]) {
            if (!jobSync.executions[0].statusCode) {
                setTimeout(() => {
                    reloadJobSync(REFERENTIAL_JOBSYNC_ID.contributor, setJobSync)
                }, 5000)
            }
        }
    }, [jobSync])

    const dispatch = useDispatch()

    const fetchData = () => dispatch(ContributorAction.fetchContributorsTable({
        searchValue: filter.searchValue,
        city: filter.citySelected?.id,
        domain: filter.domain,
        countryCode: filter.countrySelected,
        status: filter.status?.toString(),
    }))

    useEffect(() => {
        dispatch(HomeAction.setHelpLink('', ''))
        fetchData().then(() => setDataLoaded(true))

        if (globalResearch) {
            setFilter({ ...filter, searchValue: globalResearch })
            dispatch(HomeAction.updateGlobalResearch(''))
        }
        if (!cities.length) {
            dispatch(CityAction.fetchCities())
        }
        if (!sandreCodes.length) {
            dispatch(ReferencialAction.fetchSandreCodes())
        }
        if (!countries.length) {
            dispatch(CountryAction.fetchCountries())
        }
        if (!applicationSettings.length) {
            dispatch(AdministrationAction.fetchApplicationSettings())
        }
        dispatch(ContributorAction.fetchContributors(true))
        if (!referencialStatus.length) {
            dispatch(ReferencialAction.fetchReferencialStatus())
        }
    }, [])

    const onResetFilters = () => {
        setFilter({})
        dispatch(ContributorAction.fetchContributorsTable())
    }

    const internationalContext = useMemo(() => applicationSettings.find(a => a.parameter === 'ContexteInternational'), [applicationSettings])
    const isInternationalContext = useMemo(() => internationalContext ? internationalContext.value.toLowerCase() === 'oui' : false, [internationalContext])
    const countryCodeHeader = useMemo(() => isInternationalContext ? ['countryCode'] : [], [isInternationalContext])
    const headers = useMemo(() => ['siret', 'name', 'mnemonique', 'postalCode', 'city', 'phoneTel', 'domain', 'sandre', 'status', ...countryCodeHeader], [countryCodeHeader])

    const exportData = useMemo(() => getDataWithHeaders(contributorsTable, headers), [contributorsTable, headers])

    useActions(() => {
        return {
            new: () => dispatch(push(rpqs ? `${PATH_REFERENCIAL_RPQS_LINK}contributor/new` : PATH_REFERENCIAL_CONTRIBUTOR_NEW)),

            referencialActions: {
                actions: {
                    export: {
                        data: getExportReferencialData(exportData),
                        titleFile: i18n.contributorsList,
                    },
                    sync: {
                        updateAction: options => dispatch(ReferencialAction.launchSyncJob(REFERENTIAL_JOBSYNC_ID.contributor, options, () => reloadJobSync(REFERENTIAL_JOBSYNC_ID.contributor, setJobSync))),
                    },
                    purge: {
                        onPurge: () => dispatch(ContributorAction.purgeContributor()),
                    },
                },
            },
            links: [{
                href: societeDefaultUrl,
                img: societe,
                label: i18n.societe.toUpperCase(),
            }, {
                href: sandreContributorDefaultLink,
                img: logoSandre,
                label: i18n.sandre.toUpperCase(),
            }],

        }
    }, [exportData, rpqs])

    return (
        <Stack spacing={1} margin='10px 10px 10px 0'>
            <Card>
                <CardTitle title={i18n.criterias} color='bannerColor'/>
                <CardContent>
                    <Grid container spacing={1}>
                        <Grid size={8}>
                            <Input
                                title={ i18n.search }
                                value={ filter.searchValue }
                                onChange={ value => setFilter({ ...filter, searchValue: value }) }
                                onEnterKeyPress={fetchData}
                            />
                        </Grid>
                        <Grid size={4}>
                            <Select
                                options={referencialStatus}
                                label={i18n.status}
                                onChange={v => setFilter({ ...filter, status: v })}
                                value={filter.status}
                                nullLabel='&nbsp;'
                            />
                        </Grid>
                        {isInternationalContext && (
                            <Grid size={3}>
                                <Select
                                    label={i18n.country}
                                    options={countries}
                                    value={filter.countrySelected}
                                    keyValue='countryCode'
                                    keyLabel='name'
                                    onChange={value => setFilter({ ...filter, countrySelected: value })}
                                />
                            </Grid>
                        )}
                        <Grid size={3}>
                            <Select
                                options={ cities }
                                label={ i18n.city }
                                onChange={(_, value) => setFilter({ ...filter, citySelected: value })}
                                value={ filter.citySelected?.id }
                            />
                        </Grid>
                        <Grid size={3}>
                            <Select
                                label={i18n.domain}
                                options={sandreCodes.filter(s => s.field === 'INTERVENANTS.DOMAINES')}
                                value={filter.domain}
                                keyValue='code'
                                keyLabel='name'
                                onChange={value => setFilter({ ...filter, domain: value })}
                                integerValue
                            />
                        </Grid>
                        <Grid size='grow'>
                            <JobSyncStatus jobSync={jobSync}/>
                        </Grid>
                        <Grid container size='grow' justifyContent='flex-end' alignItems='center'>
                            <Grid>
                                <Button variant='outlined' onClick={onResetFilters}>
                                    {i18n.reinit}
                                </Button>
                            </Grid>
                            <Grid>
                                <Button variant='contained' onClick={fetchData}>
                                    {i18n.search}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            {dataLoaded ? (
                <CardTable
                    color='bannerColor'
                    title={ i18n.contributorsList }
                    rows={getReferencialData(contributorsTable)}
                    headers={headers}
                    onClickRow={(e) => {
                        dispatch(push(`${rpqs ? PATH_REFERENCIAL_RPQS_LINK : PATH_REFERENCIAL_LINK}contributor/${e.id}/dashboard`))
                    }}
                    rowsPerPageOptions={nbPerPageLabel}
                />
            ) : (
                <Grid padding={1}>
                    <ProgressCard indeterminate withMessage round />
                </Grid>
            )}
        </Stack>
    )
}

ContributorsPanel.propTypes = {
    rpqs: PropTypes.bool,
}

const ContributorsApp = ({ rpqs }) => {
    const {
        contributors,
    } = useSelector(store => ({
        contributors: store.ContributorReducer.contributors,
    }), shallowEqual)

    return (
        <Tabs
            defaultTab={CONTRIBUTOR}
            tabs={[{
                constant: CONTRIBUTOR,
                label: i18n.contributors,
            }, {
                constant: GROUP,
                label: i18n.contributorsGroups,
            }, {
                constant: MAP,
                label: i18n.map,
            }]}
        >
            {tab => (
                <>
                    {tab === CONTRIBUTOR && <ContributorsPanel rpqs={rpqs} />}
                    {tab === GROUP && <ContributorsGroupsApp rpqs={rpqs} />}
                    {tab === MAP && (
                        <CartographyPanel layers={['STATIONS_POINTS']}
                            componentType='all'
                            stationsPoints={contributors}
                            stationsPanelTitle={i18n.contributors}
                            heightToSubstract={450}
                            rpqs={rpqs}
                        />
                    )}
                </>
            )}
        </Tabs>
    )
}

ContributorsApp.propTypes = {
    rpqs: PropTypes.bool,
}

export default ContributorsApp
